import { useEffect, useRef } from "react";

/**
 * Logs out the user after `minutes` of inactivity.
 * Inactivity = no mouse/keyboard/scroll/touch events.
 * Shows a warning toast 60s before logout (if warnBeforeSec > 0).
 */
export default function useIdleLogout({ minutes = 30, onTimeout, onWarning, warnBeforeSec = 60, enabled = true }) {
    const timerRef = useRef(null);
    const warnRef = useRef(null);
    const lastActivity = useRef(Date.now());

    useEffect(() => {
        if (!enabled) return;

        const total = Math.max(60, minutes * 60) * 1000;
        const warnAt = Math.max(0, total - warnBeforeSec * 1000);

        const schedule = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (warnRef.current) clearTimeout(warnRef.current);
            lastActivity.current = Date.now();
            if (warnAt > 0 && onWarning) {
                warnRef.current = setTimeout(() => onWarning(warnBeforeSec), warnAt);
            }
            timerRef.current = setTimeout(() => {
                if (onTimeout) onTimeout();
            }, total);
        };

        const reset = () => schedule();
        const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
        events.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
        schedule();

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (warnRef.current) clearTimeout(warnRef.current);
            events.forEach((ev) => window.removeEventListener(ev, reset));
        };
    }, [minutes, enabled, warnBeforeSec, onTimeout, onWarning]);
}
